import { useState, useEffect, useMemo } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  UserPlus, PencilSimple, Trash, Power, Robot, MagnifyingGlass,
  ShieldCheck, Eye, EyeSlash, Sliders, Sparkle, Key, CheckSquare, Square
} from '@phosphor-icons/react';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';


const PERMISSION_OPTIONS = [
  { key: 'view_mobile', label: 'View Client Mobile No. (Unmasked)', group: 'client_data' },
  { key: 'view_user_id', label: 'View Client User ID (Unmasked)', group: 'client_data' },
  { key: 'dashboard', label: 'Dashboard', group: 'features' },
  { key: 'reports', label: 'Reports & Analytics', group: 'features' },
  { key: 'admin_chat', label: 'Admin Chat', group: 'features' },
  { key: 'credits', label: 'Credits', group: 'features' },
  { key: 'live_chat', label: 'Live Chat (Telegram)', group: 'features' },
  { key: 'whatsapp_chat', label: 'WhatsApp Chat', group: 'features' },
  { key: 'create_users', label: 'Manage Staff', group: 'features' },
  { key: 'broadcast', label: 'Broadcast', group: 'features' },
  { key: 'channel_broadcast', label: 'Channel Broadcast', group: 'features' },
  { key: 'scheduled_broadcast', label: 'Scheduled Broadcast', group: 'features' },
  { key: 'broadcast_reports', label: 'Broadcast Reports', group: 'features' },
  { key: 'user_reports', label: 'User Reports', group: 'features' },
  { key: 'mobile_reports', label: 'Mobile Reports', group: 'features' },
  { key: 'edit_bots', label: 'Edit Bots', group: 'features' },
  { key: 'bot_settings', label: 'Bot Settings', group: 'features' },
  { key: 'bot_automation', label: 'Bot Automation', group: 'features' },
  { key: 'auto_messages', label: 'Auto Messages', group: 'features' },
  { key: 'delete_chat', label: 'Delete Chats (WhatsApp & Telegram)', group: 'features' },
];

export default function AdminManagementPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create Admin Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '',
    permissions: ['live_chat', 'whatsapp_chat', 'create_users', 'dashboard', 'reports'],
    assigned_bots: [], client_data_visibility: false
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Edit Info Only Dialog
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editInfoForm, setEditInfoForm] = useState({ name: '', email: '', password: '' });
  const [savingEditInfo, setSavingEditInfo] = useState(false);

  // Dedicated Permissions Dialog
  const [permsDialogOpen, setPermsDialogOpen] = useState(false);
  const [permsAdmin, setPermsAdmin] = useState(null);
  const [permsList, setPermsList] = useState([]);
  const [permsClientData, setPermsClientData] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // Dedicated Assign Bots Dialog
  const [bots, setBots] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningAdmin, setAssigningAdmin] = useState(null);
  const [selectedBots, setSelectedBots] = useState([]);
  const [botSearch, setBotSearch] = useState('');

  // Dedicated AI Auto-Reply Dialog
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiAdmin, setAiAdmin] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiKey, setAiKey] = useState('');
  const [aiPrompt, setAiPrompt] = useState('You are a helpful assistant.');
  const [aiBots, setAiBots] = useState([]);
  const [aiAvailableBots, setAiAvailableBots] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [showAiKey, setShowAiKey] = useState(false);

  const fetchAdmins = () => {
    API.get('/admins')
      .then(r => setAdmins(r.data.admins || []))
      .catch(() => toast.error('Failed to load admins'))
      .finally(() => setLoading(false));
  };

  const fetchBots = () => {
    API.get('/bots').then(r => setBots(r.data.bots || [])).catch(() => {});
  };

  useEffect(() => {
    fetchAdmins();
    fetchBots();
  }, []);

  // --- Handlers: Create Admin ---
  const openCreate = () => {
    setCreateForm({
      name: '', email: '', password: '',
      permissions: ['live_chat', 'whatsapp_chat', 'create_users', 'dashboard'],
      assigned_bots: [], client_data_visibility: false
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    try {
      let perms = [...createForm.permissions];
      if (createForm.client_data_visibility) {
        if (!perms.includes('view_mobile')) perms.push('view_mobile');
        if (!perms.includes('view_user_id')) perms.push('view_user_id');
      } else {
        perms = perms.filter(k => k !== 'view_mobile' && k !== 'view_user_id');
      }
      await API.post('/admins', {
        ...createForm,
        permissions: perms,
        unmask_data: createForm.client_data_visibility
      });
      toast.success('Admin created successfully');
      setCreateDialogOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  // --- Handlers: Edit Info Only ---
  const openEditInfo = (admin) => {
    setEditingAdmin(admin);
    setEditInfoForm({
      name: admin.name || '',
      email: admin.email || '',
      password: ''
    });
    setEditInfoOpen(true);
  };

  const handleEditInfoSubmit = async (e) => {
    e.preventDefault();
    if (!editingAdmin) return;
    setSavingEditInfo(true);
    try {
      const update = {
        name: editInfoForm.name,
        email: editInfoForm.email,
      };
      if (editInfoForm.password?.trim()) {
        update.password = editInfoForm.password.trim();
      }
      await API.put(`/admins/${editingAdmin.id}`, update);
      toast.success('Admin info updated');
      setEditInfoOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update admin info');
    } finally {
      setSavingEditInfo(false);
    }
  };

  // --- Handlers: Permissions Dialog ---
  const openPermissions = (admin) => {
    setPermsAdmin(admin);
    const existingPerms = admin.permissions || [];
    const isVis = admin.client_data_visibility === true || existingPerms.includes('view_mobile') || existingPerms.includes('all');
    setPermsClientData(isVis);
    setPermsList([...existingPerms]);
    setPermsDialogOpen(true);
  };

  const togglePermsClientData = () => {
    setPermsClientData(prev => !prev);
  };

  const handleSavePermissions = async () => {
    if (!permsAdmin) return;
    setSavingPerms(true);
    try {
      let finalPerms = [...permsList];
      if (permsClientData) {
        if (!finalPerms.includes('view_mobile')) finalPerms.push('view_mobile');
        if (!finalPerms.includes('view_user_id')) finalPerms.push('view_user_id');
      } else {
        finalPerms = finalPerms.filter(k => k !== 'view_mobile' && k !== 'view_user_id');
      }

      await API.put(`/admins/${permsAdmin.id}`, {
        permissions: finalPerms,
        client_data_visibility: permsClientData,
        unmask_data: permsClientData
      });
      toast.success(`Permissions updated for ${permsAdmin.name}`);
      setPermsDialogOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  // --- Handlers: Assign Bots ---
  const openAssignBots = (admin) => {
    setAssigningAdmin(admin);
    setSelectedBots(admin.assigned_bots || []);
    setBotSearch('');
    setAssignDialogOpen(true);
  };

  const handleAssignBots = async () => {
    if (!assigningAdmin) return;
    try {
      await API.post(`/admins/${assigningAdmin.id}/assign-bots`, { bot_ids: selectedBots });
      toast.success(`Assigned ${selectedBots.length} bot(s) to ${assigningAdmin.name}`);
      setAssignDialogOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign bots');
    }
  };

  // --- Handlers: AI Settings ---
  const openAiSettings = async (admin) => {
    setAiAdmin(admin);
    setLoadingAi(true);
    setShowAiKey(false);
    setAiDialogOpen(true);
    try {
      const res = await API.get(`/admins/${admin.id}/ai-settings`);
      setAiEnabled(res.data.ai_enabled || false);
      setAiKey(res.data.openai_api_key || '');
      setAiPrompt(res.data.ai_prompt || 'You are a helpful assistant.');
      setAiBots(res.data.bot_ids || []);
      setAiAvailableBots(res.data.available_bots || []);
    } catch {
      toast.error('Failed to load admin AI settings');
    } finally {
      setLoadingAi(false);
    }
  };

  const handleSaveAiSettings = async () => {
    if (!aiAdmin) return;
    if (aiEnabled && !aiKey.trim()) {
      return toast.error('Please enter an OpenAI API key before enabling');
    }
    setSavingAi(true);
    try {
      await API.put(`/admins/${aiAdmin.id}/ai-settings`, {
        ai_enabled: aiEnabled,
        openai_api_key: aiKey.trim(),
        ai_prompt: aiPrompt.trim(),
        bot_ids: aiBots
      });
      toast.success(`AI Settings updated for ${aiAdmin.name}`);
      setAiDialogOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save AI settings');
    } finally {
      setSavingAi(false);
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Delete this admin?')) return;
    try {
      await API.delete(`/admins/${id}`);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const { data } = await API.post(`/admins/${id}/toggle-status`);
      toast.success(data.is_active ? 'Admin activated' : 'Admin deactivated');
      fetchAdmins();
    } catch {
      toast.error('Failed to toggle status');
    }
  };

  const filteredBots = bots.filter(b =>
    (b.bot_name || '').toLowerCase().includes(botSearch.toLowerCase()) ||
    (b.bot_username || '').toLowerCase().includes(botSearch.toLowerCase())
  );

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="admin-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Admin Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{admins.length} admin{admins.length !== 1 ? 's' : ''} registered</p>
        </div>
        <Button data-testid="add-admin-btn" onClick={openCreate} className="gap-2">
          <UserPlus size={18} /> Add Admin
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-x-auto bg-card">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Credits</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin, i) => (
              <tr key={admin.id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                <td className="px-4 py-3 font-medium">{admin.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{admin.email}</td>
                <td className="px-4 py-3">
                  <Badge variant={admin.is_active ? 'default' : 'destructive'} className="text-xs">
                    {admin.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-4 py-3 font-mono">{admin.credits || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {/* 1. Edit Info Only */}
                    <button
                      data-testid={`edit-admin-${admin.id}`}
                      onClick={() => openEditInfo(admin)}
                      className="p-1.5 rounded hover:bg-accent text-foreground transition-colors"
                      title="Edit Admin Info"
                    >
                      <PencilSimple size={16} />
                    </button>

                    {/* 2. Dedicated Permissions Button */}
                    <button
                      data-testid={`perms-admin-${admin.id}`}
                      onClick={() => openPermissions(admin)}
                      className="p-1.5 rounded hover:bg-accent text-amber-500 hover:text-amber-600 transition-colors"
                      title="Permissions & Data Visibility"
                    >
                      <ShieldCheck size={16} weight="bold" />
                    </button>

                    {/* 3. Assign Bots */}
                    <button
                      data-testid={`assign-bots-${admin.id}`}
                      onClick={() => openAssignBots(admin)}
                      className="p-1.5 rounded hover:bg-accent text-blue-500 hover:text-blue-600 transition-colors"
                      title="Assign Bots"
                    >
                      <Robot size={16} />
                    </button>

                    {/* 3.5 AI Auto-Reply Settings */}
                    <button
                      data-testid={`ai-admin-${admin.id}`}
                      onClick={() => openAiSettings(admin)}
                      className="p-1.5 rounded hover:bg-accent text-purple-500 hover:text-purple-600 transition-colors"
                      title="AI Auto-Reply Settings (OpenAI)"
                    >
                      <Sparkle size={16} weight="bold" />
                    </button>

                    {/* 4. Toggle Status */}
                    <button
                      data-testid={`toggle-admin-${admin.id}`}
                      onClick={() => toggleStatus(admin.id)}
                      className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      title="Toggle Active / Inactive"
                    >
                      <Power size={16} />
                    </button>

                    {/* 5. Delete Admin */}
                    <button
                      data-testid={`delete-admin-${admin.id}`}
                      onClick={() => deleteAdmin(admin.id)}
                      className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                      title="Delete Admin"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {admins.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No admins yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 1. EDIT INFO ONLY DIALOG (No Permissions, only basic info) */}
      <Dialog open={editInfoOpen} onOpenChange={setEditInfoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={18} className="text-primary" />
              Edit Admin Info
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditInfoSubmit} className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={editInfoForm.name}
                onChange={e => setEditInfoForm({ ...editInfoForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={editInfoForm.email}
                onChange={e => setEditInfoForm({ ...editInfoForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={editInfoForm.password}
                onChange={e => setEditInfoForm({ ...editInfoForm, password: e.target.value })}
                placeholder="Leave blank to keep unchanged"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditInfoOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingEditInfo}>{savingEditInfo ? 'Saving...' : 'Update Info'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 2. DEDICATED PERMISSIONS DIALOG */}
      <Dialog open={permsDialogOpen} onOpenChange={setPermsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-amber-500" />
              Permissions — {permsAdmin?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Client Data Visibility for Admin */}
            <div className="border border-border/80 rounded-lg p-3 bg-muted/20 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-primary" />
                  <Label className="font-semibold text-sm">Client Data Visibility for Admin</Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  data-testid="toggle-perms-client-data-btn"
                  variant={permsClientData ? "default" : "outline"}
                  onClick={togglePermsClientData}
                  className={`h-7 px-3 text-xs font-semibold gap-1.5 transition-all ${permsClientData ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-muted-foreground'}`}
                >
                  {permsClientData ? <Eye size={13} weight="bold" /> : <EyeSlash size={13} />}
                  {permsClientData ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {permsClientData
                  ? 'Enabled: This admin can see unmasked client data (phone numbers and user IDs), and can grant visibility to their staff.'
                  : 'Disabled: Client data will be masked with asterisks.'}
              </p>
            </div>

            {/* Feature Permissions */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3">
              <Label className="font-semibold text-sm">Feature Permissions</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {PERMISSION_OPTIONS.filter(p => p.group === 'features').map(p => (
                  <label
                    key={p.key}
                    htmlFor={`perm-check-${p.key}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer border border-border/40 select-none"
                  >
                    <Checkbox
                      id={`perm-check-${p.key}`}
                      checked={permsList.includes(p.key)}
                      onCheckedChange={(checked) => {
                        setPermsList(prev => checked ? [...prev, p.key] : prev.filter(k => k !== p.key));
                      }}
                    />
                    <span className="text-xs cursor-pointer flex-1 truncate font-medium">
                      {p.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setPermsDialogOpen(false)}>Cancel</Button>
              <Button type="button" onClick={handleSavePermissions} disabled={savingPerms}>
                {savingPerms ? 'Saving...' : 'Save Permissions'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* 3. ADD ADMIN DIALOG */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} className="text-primary" />
              Add New Admin
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  data-testid="admin-name-input"
                  value={createForm.name}
                  onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={createForm.email}
                  onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={createForm.password}
                onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                required
                minLength={6}
              />
            </div>

            {/* Client Data Visibility for Admin */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-primary" />
                  <Label className="font-semibold text-sm">Client Data Visibility for Admin</Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={createForm.client_data_visibility ? "default" : "outline"}
                  onClick={() => setCreateForm(prev => ({ ...prev, client_data_visibility: !prev.client_data_visibility }))}
                  className={`h-7 px-3 text-xs font-semibold gap-1.5 transition-all ${createForm.client_data_visibility ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-muted-foreground'}`}
                >
                  {createForm.client_data_visibility ? <Eye size={13} weight="bold" /> : <EyeSlash size={13} />}
                  {createForm.client_data_visibility ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {createForm.client_data_visibility
                  ? 'Enabled: This admin can see unmasked client data (phone numbers and user IDs).'
                  : 'Disabled: Client data will be masked with asterisks.'}
              </p>
            </div>

            {/* Feature Permissions */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3">
              <Label className="font-semibold text-sm">Feature Permissions</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {PERMISSION_OPTIONS.filter(p => p.group === 'features').map(p => (
                  <label
                    key={p.key}
                    htmlFor={`create-perm-${p.key}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer border border-border/40 select-none"
                  >
                    <Checkbox
                      id={`create-perm-${p.key}`}
                      checked={createForm.permissions?.includes(p.key)}
                      onCheckedChange={(checked) => {
                        setCreateForm(prev => {
                          const perms = prev.permissions || [];
                          const updated = checked ? [...perms, p.key] : perms.filter(k => k !== p.key);
                          return { ...prev, permissions: updated };
                        });
                      }}
                    />
                    <span className="text-xs cursor-pointer flex-1 truncate font-medium">
                      {p.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Assign Bots */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-sm">Assign Bots ({createForm.assigned_bots?.length || 0} selected)</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCreateForm({ ...createForm, assigned_bots: bots.map(b => b.id) })} className="text-xs text-primary hover:underline">Select All</button>
                  <button type="button" onClick={() => setCreateForm({ ...createForm, assigned_bots: [] })} className="text-xs text-muted-foreground hover:underline">Clear</button>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1.5 pt-1">
                {bots.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">No bots available</p>
                ) : (
                  bots.map(bot => (
                    <label
                      key={bot.id}
                      htmlFor={`create-bot-${bot.id}`}
                      className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/40 cursor-pointer border border-border/40 select-none"
                    >
                      <Checkbox
                        id={`create-bot-${bot.id}`}
                        checked={createForm.assigned_bots?.includes(bot.id)}
                        onCheckedChange={(checked) => {
                          setCreateForm(prev => {
                            const assigned = prev.assigned_bots || [];
                            const updated = checked ? [...assigned, bot.id] : assigned.filter(id => id !== bot.id);
                            return { ...prev, assigned_bots: updated };
                          });
                        }}
                      />
                      <span className="text-xs font-medium cursor-pointer flex-1">
                        {bot.bot_name} <span className="text-[11px] text-muted-foreground font-mono ml-1">(@{bot.bot_username})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button data-testid="admin-submit-btn" type="submit" disabled={creatingAdmin}>
                {creatingAdmin ? 'Creating...' : 'Create Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 4. DEDICATED ASSIGN BOTS DIALOG */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Robot size={20} className="text-primary" />
              Assign Bots to {assigningAdmin?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search bots..."
                value={botSearch}
                onChange={e => setBotSearch(e.target.value)}
                className="pl-8 text-xs h-8"
              />
            </div>

            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-muted-foreground">{selectedBots.length} of {bots.length} selected</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelectedBots(bots.map(b => b.id))} className="text-primary hover:underline">Select All</button>
                <button type="button" onClick={() => setSelectedBots([])} className="text-muted-foreground hover:underline">Clear</button>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1.5 border border-border rounded-md p-2">
              {filteredBots.map(bot => (
                <label
                  key={bot.id}
                  htmlFor={`assign-dialog-bot-${bot.id}`}
                  className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/50 cursor-pointer border border-border/40 select-none"
                >
                  <Checkbox
                    id={`assign-dialog-bot-${bot.id}`}
                    checked={selectedBots.includes(bot.id)}
                    onCheckedChange={(checked) => {
                      setSelectedBots(prev => checked ? [...prev, bot.id] : prev.filter(id => id !== bot.id));
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{bot.bot_name}</p>
                    <p className="text-[10px] text-muted-foreground font-mono truncate">@{bot.bot_username}</p>
                  </div>
                </label>
              ))}
              {filteredBots.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">No bots found</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button type="button" onClick={handleAssignBots}>Save Assignments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4. DEDICATED AI AUTO-REPLY SETTINGS DIALOG */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} className="text-purple-500" />
              AI Auto-Reply (OpenAI) — {aiAdmin?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingAi ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading AI settings...</div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between border border-border p-3 rounded-md bg-muted/20">
                <div>
                  <Label className="font-semibold text-sm">Enable AI Auto-Reply</Label>
                  <p className="text-xs text-muted-foreground">Automatically answer user messages using OpenAI</p>
                </div>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={setAiEnabled}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Key size={14} className="text-amber-500" />
                  OpenAI API Key
                </Label>
                <div className="relative">
                  <Input
                    type={showAiKey ? 'text' : 'password'}
                    value={aiKey}
                    onChange={e => setAiKey(e.target.value)}
                    placeholder="sk-proj-..."
                    className="pr-10 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAiKey(!showAiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showAiKey ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">API key from platform.openai.com</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  AI System Prompt
                </Label>
                <Textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder="You are a helpful assistant..."
                  rows={3}
                  className="text-xs leading-relaxed"
                />
              </div>

              <div className="border border-border/80 rounded-lg p-3 bg-muted/10 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Apply to Assigned Bots ({aiBots.length} of {aiAvailableBots.length} selected)
                  </Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAiBots(aiAvailableBots.map(b => b.id))}
                      className="text-xs text-primary hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiBots([])}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {aiAvailableBots.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">No assigned bots for this admin yet</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1.5 pt-1">
                    {aiAvailableBots.map(bot => {
                      const isChecked = aiBots.includes(bot.id);
                      return (
                        <label
                          key={bot.id}
                          className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/50 cursor-pointer border border-border/40 select-none text-xs"
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => {
                              setAiBots(prev => isChecked ? prev.filter(id => id !== bot.id) : [...prev, bot.id]);
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-foreground">{bot.bot_name}</span>
                            <span className="text-[11px] text-muted-foreground ml-2 font-mono">@{bot.bot_username}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setAiDialogOpen(false)}>Cancel</Button>
                <Button type="button" onClick={handleSaveAiSettings} disabled={savingAi}>
                  {savingAi ? 'Saving...' : 'Save AI Settings'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>

  );
}
